import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { type FamilyLanguage, useLanguageCatalogue } from '../lib/languages';
import { useAction, useApi } from '../lib/useApi';
import { resetTerms } from '../lib/terms';
import { ErrorBox, Loading, Notice } from './ui';
import { LanguageBadge, LanguageChecklist, LanguageNote } from './LanguagePicker';

/**
 * "What is spoken in your home?" — family setup and settings.
 *
 * A family can name every language its elders speak. All of them work for the Story Vault, the
 * Voice Tree and Grandparent Calls from the moment they are chosen. Lessons follow only where
 * they have actually been written, and the badges say so.
 */
export function FamilyLanguages({ canEdit }: { canEdit: boolean }) {
  const { data, error, loading, reload } = useApi<{ languages: FamilyLanguage[] }>('/family/languages');
  const { byCode } = useLanguageCatalogue();
  const act = useAction();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [primary, setPrimary] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setSelected(data.languages.map((l) => l.code));
    setPrimary(data.languages.find((l) => l.is_primary)?.code ?? data.languages[0]?.code ?? null);
  }, [data]);

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;

  const current = data?.languages ?? [];
  const toggle = (code: string) =>
    setSelected((s) => {
      const next = s.includes(code) ? s.filter((c) => c !== code) : [...s, code];
      if (!next.includes(primary ?? '')) setPrimary(next[0] ?? null);
      return next;
    });

  return (
    <section className="card stack" aria-label="Your family’s languages">
      <div className="spread">
        <h2 style={{ margin: 0 }}>Your family’s languages</h2>
        {canEdit && !editing && (
          <button className="btn small secondary" onClick={() => setEditing(true)}>
            Change
          </button>
        )}
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        Stories, elders’ voices and Grandparent Calls work in every language on the list, today. Lessons and games are
        written for Yorùbá first.
      </p>

      {!editing && (
        <div className="stack">
          {current.length === 0 && <p className="muted">No home language chosen yet.</p>}
          {current.map((l) => (
            <div key={l.code} className="stack" style={{ gap: 4 }}>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <strong>{l.name}</strong>
                <span className="yo" lang={l.code} dir={l.rtl ? 'rtl' : 'ltr'}>
                  {l.native_name}
                </span>
                {l.is_primary && <span className="badge">main at home</span>}
                <LanguageBadge language={l} />
              </div>
              {l.status !== 'lessons_live' && <LanguageNote language={byCode.get(l.code) ?? l} />}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await act.run(
              async () => {
                const r = await api('/family/languages', { body: { codes: selected, primary: primary ?? selected[0] } });
                // The interface words follow the home language, so drop the cache
                // and let the next screen pick up the new one.
                resetTerms();
                return r;
              },
              'Saved. Your stories can be recorded in any of these.',
            );
            if (ok) {
              setEditing(false);
              reload();
            }
          }}
        >
          <LanguageChecklist selected={selected} primary={primary} onToggle={toggle} onPrimary={setPrimary} />
          <ErrorBox error={act.error} />
          <div className="row">
            <button className="btn small" disabled={act.busy || selected.length === 0}>
              {act.busy ? 'Saving…' : 'Save languages'}
            </button>
            <button type="button" className="btn small ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
          {selected.length === 0 && <span className="hint">Choose at least one language.</span>}
        </form>
      )}
      <Notice text={act.notice} />
    </section>
  );
}
